import { map, values, mapObjIndexed, join, path } from "ramda";
import numeral from "numeral";
import "numeral/locales/de";

import i18n from "@/i18n";
import reportHeader from "@/utils/pdf/header";
import groupByCategory from "@/utils/Requirements/groupByCategory";
import fundInfo from "@/utils/pdf/fundInfo";
import { getDisclaimer } from "@/utils/getDisclaimer";
import markdownToPdfmake from "@fng/common/markdownToPdfmake";

const h1 = (text) => ({
  text,
  bold: true,
  fontSize: 18,
  margin: [0, 0, 0, 18],
  pageBreak: "before",
});

const h3 = (text) => ({
  text,
  bold: true,
  fontSize: 12,
  margin: [0, 0, 0, 5],
});

const responseList = (responses, fund) => {
  if (!responses || !responses.length) return null;

  return [
    {
      table: {
        widths: [60, "*"],
        body: map((response) => {
          const type =
            response.type === "USER"
              ? i18n.t("responseList.user", fund.language)
              : i18n.t("responseList.auditor", fund.language);

          const files = response.attachments.length
            ? "\n\n---\n" + join("\n", map(path(["file", "name"]), response.attachments))
            : "";

          return [
            `${type}\n${new Date(response.updatedAt).toLocaleString(undefined, {
              timeZone: "CET",
            })}`,
            [...markdownToPdfmake(response.text), ...(files ? [files] : [])],
          ];
        }, responses),
      },
    },
  ];
};

const feedback = (requirement, fund) => {
  const body = [];

  if (requirement.userFeedback)
    body.push([i18n.t("userFeedback.title", fund.language), markdownToPdfmake(requirement.userFeedback)]);

  if (requirement.auditorFeedback)
    body.push([i18n.t("auditorFeedback.title", fund.language), markdownToPdfmake(requirement.auditorFeedback)]);

  if (body.length === 0) return null;

  return [
    {
      margin: [0, 5, 0, 0],
      table: {
        widths: [60, "*"],
        body,
      },
    },
  ];
};

const minimalRequirements = (fund, requirements, audit) => {
  const categories = groupByCategory(requirements);

  const header = h1(i18n.t("fund.protocol.minimalRequirements.title", fund.language));

  const widths = audit ? [150, "*", 100, 100, 100] : [150, "*"];

  const cats = values(
    mapObjIndexed((category, index) => {
      return [
        h3(i18n.t(`minimalRequirementCategory.${index}.name`, fund.language)),
        {
          style: "requirementTable",
          margin: [0, 0, 0, 20],
          table: {
            widths,
            body: map((requirement) => {
              const row = [
                [
                  [
                    {
                      text: i18n.t(`minimalRequirement.${requirement.requirementId}.name`, fund.language),
                      bold: true,
                    },
                    {
                      text: i18n.t(`minimalRequirement.${requirement.requirementId}.description`, fund.language),
                      bold: false,
                    },
                  ],
                ],
                [responseList(requirement.protocol, fund), feedback(requirement, fund)],
              ];

              if (audit) {
                row.push([requirement.result.result, ...markdownToPdfmake(requirement.result.description)]);
                row.push(markdownToPdfmake(requirement.internalComment));
                row.push(markdownToPdfmake(requirement.notReferencedInternalComment));
              }

              return row;
            }, category),
          },
        },
      ];
    }, categories)
  );

  return [header, ...cats];
};

const stepModelRequirements = (fund, requirements, audit) => {
  const categories = groupByCategory(requirements);

  const header = h1(i18n.t("fund.protocol.stepModelRequirements.title", fund.language));

  const widths = audit ? [150, "*", 100, 100, 100] : [150, "*"];

  const cats = values(
    mapObjIndexed((category, index) => {
      return [
        h3(i18n.t(`stepModelRequirementCategory.${index}.name`, fund.language)),
        {
          style: "requirementTable",
          margin: [0, 0, 0, 20],
          table: {
            widths,
            body: map((requirement) => {
              const row = [
                [
                  [
                    {
                      text: i18n.t(`stepModelRequirement.${requirement.requirementId}.name`, fund.language),
                      bold: true,
                    },
                    {
                      text: i18n.t(`stepModelRequirement.${requirement.requirementId}.description`, fund.language),
                      bold: false,
                    },
                  ],
                ],
                [responseList(requirement.protocol, fund), feedback(requirement, fund)],
              ];

              if (audit) {
                row.push([requirement.result.points, ...markdownToPdfmake(requirement.result.description)]);
                row.push(markdownToPdfmake(requirement.internalComment));
                row.push(markdownToPdfmake(requirement.notReferencedInternalComment));
              }

              return row;
            }, category),
          },
        },
      ];
    }, categories)
  );

  return [header, ...cats];
};

const generateProtocolPdfData = (fund, audit = false) => {
  numeral.locale(fund.language);

  const name = {
    text: i18n.t(`fund.protocol.title`, fund.language, {
      year: fund.auditPeriod.year + 1,
    }),
    bold: true,
    fontSize: 20,
    alignment: "center",
  };

  let result = minimalRequirements(fund, fund.minimalRequirements, audit);

  if (fund.stepModel) {
    result = result.concat(stepModelRequirements(fund, fund.stepModelRequirements, audit));
  }

  const disclaimer = {
    text: getDisclaimer(fund),
    fontSize: 6,
    color: "#888888",
    margin: [20, 10, 20, 20],
  };

  return {
    footer: (currentPage, pageCount) => [
      {
        text: `${currentPage.toString()}/${pageCount.toString()}`,
        alignment: "center",
        margin: [0, 20, 0, 0],
      },
      disclaimer,
    ],
    content: [name, reportHeader, fundInfo(fund), result],
    defaultStyle: {
      fontSize: 6,
    },
    pageMargins: [20, 20, 20, 90],
    pageOrientation: "landscape",
  };
};

export default generateProtocolPdfData;
